import { useState, useMemo } from "react";
import { Loader2, Layers, X, RefreshCw } from "lucide-react";
import { academySkillsAPI, getApiError } from "../../../services/academyService";

/**
 * target = null  → إنشاء مهارة مجمّعة جديدة
 * target = skill → تحديث المهارة المجمّعة (بيضيف الجديد بس)
 */
export default function MergeMixedModal({ examId, candidates, target, onClose, onDone }) {
  const isUpdate = !!target;
  const [selected, setSelected] = useState(() => new Set(candidates.map((s) => s.id)));
  const [title, setTitle] = useState("كل المسارات الميكسيد");
  const [deactivate, setDeactivate] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedIds = useMemo(() => [...selected], [selected]);

  const resetPreview = () => setPreview(null);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    resetPreview();
  };

  const buildPayload = (dryRun) => ({
    source_skill_ids: selectedIds,
    deactivate_sources: deactivate,
    dry_run: dryRun,
    ...(isUpdate ? { target_skill_id: target.id } : { title }),
  });

  const run = async (dryRun) => {
    if (selectedIds.length === 0) {
      setError("اختار مهارة واحدة على الأقل");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const data = await academySkillsAPI.mergeMixed(examId, buildPayload(dryRun));
      if (dryRun) setPreview(data);
      else onDone(data);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4" dir="rtl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            {isUpdate ? <RefreshCw className="w-5 h-5 text-indigo-600" /> : <Layers className="w-5 h-5 text-indigo-600" />}
            {isUpdate ? `تحديث: ${target.title}` : "دمج المهارات الميكسيد"}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-sm text-gray-500">
          {isUpdate
            ? "هيتم نسخ الأسئلة الجديدة فقط للمهارة المجمّعة، وكل اللي اتنسخ قبل كده هيتخطى."
            : "هيتم إنشاء مهارة ميكسيد جديدة ونسخ أسئلة المهارات المختارة فيها بالترتيب."}
        </p>

        {!isUpdate && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">عنوان المهارة المجمّعة</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        )}

        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">المهارات المصدر ({selected.size}/{candidates.length})</p>
          <div className="space-y-1.5 max-h-52 overflow-y-auto border border-gray-200 rounded-xl p-2">
            {candidates.map((s) => (
              <label key={s.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer">
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                  <span className="text-sm text-gray-800">{s.title}</span>
                  {!s.is_active && <span className="text-xs text-gray-400">(غير نشطة)</span>}
                </span>
                <span className="text-xs text-gray-400">{s.total_questions || 0} سؤال</span>
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={deactivate}
            onChange={(e) => { setDeactivate(e.target.checked); resetPreview(); }}
          />
          تعطيل المهارات الأصلية بعد النسخ (عشان الطالب ميشوفش الأسئلة مرتين)
        </label>

        {preview && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-sm space-y-1">
            <p className="font-semibold text-indigo-800">معاينة: هيتنسخ {preview.total_copied} عنصر</p>
            {preview.sources.map((r) => (
              <p key={r.skill_id} className="text-indigo-700">
                {r.title}: {r.copied} جديد · {r.skipped} متخطى
              </p>
            ))}
            {preview.total_copied === 0 && (
              <p className="text-amber-700">مفيش حاجة جديدة تتنسخ.</p>
            )}
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}

        <div className="flex items-center justify-start gap-2 pt-2">
          {preview ? (
            <button
              onClick={() => run(false)}
              disabled={loading || preview.total_copied === 0}
              className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              تأكيد وتنفيذ
            </button>
          ) : (
            <button
              onClick={() => run(true)}
              disabled={loading}
              className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-xl hover:bg-gray-800 disabled:opacity-50 text-sm font-medium"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              معاينة أولاً
            </button>
          )}
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-300 text-sm hover:bg-gray-50">
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}